import type { Promotion } from './promotions';

/**
 * Is this offer running right now?
 *
 * Until this existed, `listActivePublic` filtered on `is_active` alone, so a happy hour
 * configured for Mon–Thu 16:00–19:00 was shown to guests at 2am on a Sunday. The window
 * the owner had carefully set was decoration: displayed as text, enforced nowhere.
 *
 * Shared rather than written twice, so the dashboard's "live now" indicator and the guest
 * landing page cannot disagree about whether an offer is running — a preview that says
 * something different from the guest's phone is worse than no preview.
 */

/** `valid_days` accepts both 'mon' and 'monday'; normalise before comparing. */
const DAY_INDEX: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tuesday: 2,
  wed: 3, wednesday: 3,
  thu: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

/** Minutes since midnight for 'HH:MM' / 'HH:MM:SS'. Null when unparseable. */
export function minutesOfDay(time: string | null | undefined): number | null {
  if (!time) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** The venue's own wall clock: local date (YYYY-MM-DD), weekday and minutes since midnight. */
export interface VenueNow {
  date: string;
  /** 0 = Sunday, matching `Date.getDay()`. */
  weekday: number;
  minutes: number;
}

/**
 * What time it is AT THE VENUE.
 *
 * Everything here is judged in the venue's timezone, never the guest's or the server's. A
 * guest in London looking at a Dubai venue must see Dubai's happy hour, and a server in
 * UTC must not end it four hours early.
 */
export function venueNow(at: Date, timezone: string): VenueNow {
  // `en-CA` gives YYYY-MM-DD, which sorts and compares as a string.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', weekday: 'short',
    hour12: false,
  });

  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(at)) parts[p.type] = p.value;

  // `hour` is '24' at midnight in some ICU versions; normalise to 0.
  const hour = Number(parts.hour) % 24;

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    weekday: DAY_INDEX[(parts.weekday ?? '').toLowerCase()] ?? 0,
    minutes: hour * 60 + Number(parts.minute),
  };
}

/** The date `days` before `date` (YYYY-MM-DD), as YYYY-MM-DD. */
function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Is `now` inside the daily time window?
 *
 * Handles a window that CROSSES MIDNIGHT (22:00–02:00), which is not an edge case for bars
 * — it is the normal shape of a late happy hour. Treating it as a plain `from <= now <= to`
 * comparison makes such a window match nothing at all, silently.
 *
 * Returns which day the window STARTED on, because that is the day `valid_days` must be
 * checked against: at 01:00 on Saturday, a Friday 22:00–02:00 window is still Friday's.
 */
function timeWindow(
  now: VenueNow,
  from: number | null,
  to: number | null,
): { live: boolean; startedOnWeekday: number; startedOnDate: string } {
  const sameDay = { live: true, startedOnWeekday: now.weekday, startedOnDate: now.date };

  // No window, or a half-open one: treat as all day. An owner who filled in only a start
  // time meant "from then on", not "for zero minutes".
  if (from == null && to == null) return sameDay;
  if (from != null && to == null) return { ...sameDay, live: now.minutes >= from };
  if (from == null && to != null) return { ...sameDay, live: now.minutes < to };

  const start = from as number;
  const end = to as number;

  if (start < end) return { ...sameDay, live: now.minutes >= start && now.minutes < end };

  // Crossing midnight. Before the end time we are in YESTERDAY's window.
  if (now.minutes < end) {
    return {
      live: true,
      startedOnWeekday: (now.weekday + 6) % 7,
      startedOnDate: shiftDate(now.date, -1),
    };
  }
  return { ...sameDay, live: now.minutes >= start };
}

/**
 * Should this promotion be shown to a guest right now?
 *
 * **Bookable and non-bookable are judged differently, and getting this backwards would
 * break bookings.** For an OFFER the window means "when you can claim it", so outside it
 * there is nothing to show. For an EVENT the same fields mean "when the event happens" —
 * a Monday brunch must stay visible on Sunday, or nobody can ever book it in advance.
 *
 * So an event is hidden only once its last date has passed; an offer must be live now.
 */
export function isPromotionVisible(promo: Promotion, now: VenueNow): boolean {
  if (!promo.is_active) return false;

  // Past its last day: nothing left to claim or to book, either way.
  const end = promo.valid_to_date?.slice(0, 10);
  if (end && end < now.date) return false;

  // An event stays visible ahead of time — that is the entire point of taking bookings.
  if (promo.is_bookable) return true;

  const start = promo.valid_from_date?.slice(0, 10);
  if (start && start > now.date) return false;

  const window = timeWindow(
    now,
    minutesOfDay(promo.valid_from_time),
    minutesOfDay(promo.valid_to_time),
  );
  if (!window.live) return false;

  // A window that began yesterday must not run past its own end date.
  if (end && window.startedOnDate > end) return false;
  if (start && window.startedOnDate < start) return false;

  const days = promo.valid_days ?? [];
  if (days.length === 0) return true;

  return days.some(d => DAY_INDEX[d.trim().toLowerCase()] === window.startedOnWeekday);
}
